/**
 * Sync protocol (plan §8, docs/SYNC.md): the messages exchanged between devices, hubs and the
 * server. The same protocol is spoken on the LAN (client ↔ hub) and over the internet
 * (hub or client ↔ server), so a client can switch between them at any moment.
 */
import { z } from 'zod';

import type { LocalizedText } from '../errors/errors.ts';

export const SYNC_LIMITS = {
  /** Commands per push. */
  pushBatch: 100,
  /** Changes per pull. */
  pullBatch: 500,
  /** Entities per bootstrap page. */
  bootstrapPage: 500,
  /** Command ids a device may ask about in one pull. */
  awaiting: 500,
  /** Explicit dependencies of one command. */
  dependsOn: 20,
} as const;

const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const commandType = z.string().regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/);

/** One command: what a user did on a device, with everything needed to replay it exactly once. */
export const CommandEnvelopeSchema = z.object({
  /** UUIDv7 generated on the device. */
  commandId: z.string().regex(UUID_V7, 'must be a UUIDv7'),
  /** Same key → same effect, never a second one. */
  idempotencyKey: z.string().min(8).max(200),
  type: commandType,
  version: z.number().int().min(1).max(1000),
  payload: z.record(z.string(), z.unknown()),
  /** Device that created the command. */
  deviceId: z.uuid(),
  userId: z.uuid(),
  locationId: z.uuid(),
  /** Device clock when the user acted (ISO 8601 with offset). */
  occurredAt: z.iso.datetime({ offset: true }),
  /** 1, 2, 3… per device, with no gaps: the server applies a device's commands in this order. */
  clientSeq: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
  /** Commands (of any device) that must be applied before this one. */
  dependsOn: z.array(z.uuid()).max(SYNC_LIMITS.dependsOn),
  /** Made in an offline session: only the offline allow-list applies (plan §7.4). */
  offline: z.boolean(),
});
export type CommandEnvelope = z.infer<typeof CommandEnvelopeSchema>;

export const PushRequestSchema = z.object({
  /** Clock of the pushing device when it sent the request (clock-skew detection). */
  sentAt: z.iso.datetime({ offset: true }),
  commands: z
    .array(
      z.object({
        /** Validated one by one, so one malformed command never blocks the others. */
        envelope: z.unknown(),
        /** Only from a hub: origin device clock − hub clock, measured when the hub received it. */
        originSkewMs: z.number().int().optional(),
      }),
    )
    .max(SYNC_LIMITS.pushBatch),
});
export type PushRequest = z.infer<typeof PushRequestSchema>;

export interface SyncError {
  readonly code: string;
  readonly message: LocalizedText;
  readonly details?: unknown;
}

/**
 * - `ACCEPTED` / `REJECTED`: final (the server decided). `duplicate: true` when the command had
 *   already been processed: this is the original outcome, and nothing happened a second time.
 * - `STORED`: a hub has saved it durably; the server has not decided yet. The client keeps it
 *   until the server's decision comes back (owner rule: nothing is lost if the hub dies).
 * - `DEFERRED`: not processed now (missing earlier command, pending dependency, server busy);
 *   the client keeps it and tries again.
 */
export type PushStatus = 'ACCEPTED' | 'REJECTED' | 'STORED' | 'DEFERRED';
export type DeferReason = 'SEQ_GAP' | 'DEPENDENCY_PENDING' | 'RETRY';

export interface PushResult {
  readonly commandId: string;
  readonly status: PushStatus;
  readonly duplicate?: boolean;
  readonly result?: unknown;
  readonly error?: SyncError;
  readonly deferReason?: DeferReason;
  /** The device clock differs from the server by more than the allowed skew. */
  readonly clockSkewFlagged?: boolean;
}

export interface PushResponse {
  readonly serverTime: string;
  readonly results: readonly PushResult[];
  /** Pushing device clock − server clock (ms), as measured by the receiver. */
  readonly clockSkewMs: number;
}

export const PullRequestSchema = z.object({
  /** Highest seq already applied by the device (0 = nothing yet). */
  cursor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  limit: z.number().int().min(1).max(SYNC_LIMITS.pullBatch).optional(),
  /** Commands the device is still waiting to hear about (stored at a hub, or pushed earlier). */
  awaiting: z.array(z.uuid()).max(SYNC_LIMITS.awaiting).optional(),
  /**
   * A phone whose local database is encrypted says so (W4, D184). A phone that does not receives
   * nothing from the feed (D196); PCs ignore it.
   */
  storeEncrypted: z.boolean().optional(),
});
export type PullRequest = z.infer<typeof PullRequestSchema>;

export interface Change {
  readonly seq: number;
  readonly entityType: string;
  readonly entityId: string;
  readonly op: 'UPSERT' | 'DELETE';
  readonly data: Record<string, unknown> | null;
}

/** What the receiver knows about a command the device is waiting for. */
export interface AwaitedResult {
  readonly commandId: string;
  /** `PENDING`: stored at the hub, not decided yet. `UNKNOWN`: never received — push it again. */
  readonly status: 'ACCEPTED' | 'REJECTED' | 'PENDING' | 'UNKNOWN';
  readonly result?: unknown;
  readonly error?: SyncError;
}

export interface PullResponse {
  readonly serverTime: string;
  readonly changes: readonly Change[];
  /** Next cursor to send. */
  readonly cursor: number;
  readonly hasMore: boolean;
  /** The device's cursor is older than the retained feed: bootstrap again. */
  readonly resync: boolean;
  readonly results: readonly AwaitedResult[];
  /** Hub only: commands of other devices at this location not yet confirmed by the server. */
  readonly pending?: readonly CommandEnvelope[];
}

export const BootstrapRequestSchema = z.object({
  pageToken: z.string().max(2000).optional(),
  /** Entities per page (default and maximum: SYNC_LIMITS.bootstrapPage). */
  limit: z.number().int().min(1).max(SYNC_LIMITS.bootstrapPage).optional(),
  /** As in a pull: a phone with an encrypted database (W4, D184). */
  storeEncrypted: z.boolean().optional(),
});
export type BootstrapRequest = z.infer<typeof BootstrapRequestSchema>;

export interface SnapshotEntity {
  readonly entityType: string;
  readonly entityId: string;
  readonly data: Record<string, unknown>;
}

export interface BootstrapResponse {
  /** Continue pulling from this seq once every page is applied. */
  readonly snapshotSeq: number;
  readonly entities: readonly SnapshotEntity[];
  readonly nextPageToken: string | null;
}

/** Devices of a hub's location, so the hub can authenticate LAN clients while offline. */
export interface HubCredentialsResponse {
  readonly devices: readonly {
    readonly id: string;
    readonly type: string;
    readonly status: 'ACTIVE' | 'REVOKED';
    /** SHA-256 of the device secret (the secret itself never leaves the device). */
    readonly credentialHash: string;
  }[];
}

/**
 * A phone's local database, as it tells the server at each check-in (W4-4, D285), so IT sees which
 * phones did not move to the encrypted store and why. MOVE_FAILED: the phone keeps sending from its
 * old store and tries again at its next start; KEY_LOST / STORE_UNREADABLE: its encrypted store
 * cannot be opened (the key is gone, or the file does not open with it) — the person is asked to sign
 * in again (D286); FOLDER_UNREADABLE: the phone cannot read its database folder.
 */
export const PHONE_STORE_STATES = [
  'ENCRYPTED',
  'MOVE_FAILED',
  'KEY_LOST',
  'STORE_UNREADABLE',
  'FOLDER_UNREADABLE',
] as const;
export type PhoneStoreState = (typeof PHONE_STORE_STATES)[number];

export const PhoneStoreReportSchema = z.object({
  state: z.enum(PHONE_STORE_STATES),
  /** Why, in a few words (an error message from the phone; never a person's data). */
  detail: z.string().trim().max(300).optional(),
});
export type PhoneStoreReport = z.infer<typeof PhoneStoreReportSchema>;

/**
 * Where an upload of a photo kept on a device stopped (W4-9, D294): asking the server for an upload
 * link, sending the bytes to storage, or asking the server to check them.
 */
export const PHOTO_UPLOAD_STEPS = ['link', 'upload', 'confirm'] as const;
export type PhotoUploadStep = (typeof PHOTO_UPLOAD_STEPS)[number];

/**
 * The photos a device still keeps for the server, as it says at check-in (W4-9, owner O63; D293):
 * how many wait, and — once they have failed for a few minutes while the device was online — why.
 * `problem: null` says they go (or nothing waits). Never a photo's content or a person's data.
 */
export const DevicePhotoReportSchema = z.object({
  waiting: z.number().int().min(0).max(100_000),
  problem: z
    .object({
      step: z.enum(PHOTO_UPLOAD_STEPS),
      /** The error, in a few words (a message or an HTTP status and code). */
      detail: z.string().trim().min(1).max(300),
      /** When the uploads started failing. */
      since: z.iso.datetime({ offset: true }),
    })
    .nullable(),
});
export type DevicePhotoReport = z.infer<typeof DevicePhotoReportSchema>;

/** Why a phone's encrypted store could not be opened (D286). */
export const LOST_STORE_KINDS = ['KEY_LOST', 'STORE_UNREADABLE'] as const;
export type LostStoreKind = (typeof LOST_STORE_KINDS)[number];

/**
 * A store this phone could not open, reported with the sign-in that replaces it (D286): the
 * registration it belonged to (from its file name) and why. The server records it before the phone
 * may delete the file.
 */
export const LostStoreSchema = z.object({
  deviceId: z.uuid(),
  kind: z.enum(LOST_STORE_KINDS),
  detail: z.string().trim().max(300).optional(),
});
export type LostStore = z.infer<typeof LostStoreSchema>;

/** Headers carrying the device credential on every sync request. */
export const DEVICE_ID_HEADER = 'x-device-id';
export const DEVICE_KEY_HEADER = 'x-device-key';

/** JSON with object keys sorted at every level: the same value always gives the same text. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(source)
        .sort()
        .map((key) => [key, sortKeys(source[key])]),
    );
  }
  return value;
}

/** The exact content of a command, for "same id or key, different content" detection. */
export function envelopeFingerprint(envelope: CommandEnvelope): string {
  return canonicalJson({
    ...envelope,
    occurredAt: new Date(envelope.occurredAt).toISOString(),
    dependsOn: [...envelope.dependsOn].sort(),
  });
}
